// src/api/blender.ts
import { api } from "./client";

export type ExtrudersStatusResponse = {
  /** Carriage position only — "in" does not mean the screw is turning. */
  extruder1?: string; // "in" | "out"
  extruder2?: string;
  /** Motion. Classify against the thresholds in /settings/extruders. */
  extruder1_speed_hz?: number | null;
  extruder2_speed_hz?: number | null;
  extruder1_current_a?: number | null;
  extruder2_current_a?: number | null;
  timestamp?: string;
  /**
   * How long since blender 1's PLC — which the extruders sit on — was last
   * read. Row age will not do: plc-worker skips unchanged readings.
   */
  seconds_since_read?: number | null;
  /** The PLC has not been read inside the server's cutoff: unreachable. */
  stale?: boolean;
  [k: string]: unknown;
};

/** Per-blender latest reading. `stale` means the row is too old to trust. */
export type BlenderStatusEntry = {
  status?: "running" | "idle" | "stopped" | "unknown";
  /** Age of that reading, computed server-side. */
  seconds_since?: number;
  stale?: boolean;
  timestamp?: string;
};

export type BlendersStatusResponse = {
  "1"?: BlenderStatusEntry;
  "2"?: BlenderStatusEntry;
  [k: string]: unknown;
};

export type UtilizationResponse = {
  [blenderId: string]: {
    utilization_percent: number;
    stopped: number;
    idle: number;
    running: number;
  };
};

export function getExtrudersCurrentStatus(signal?: AbortSignal) {
  return api
    .get<ExtrudersStatusResponse>("/blender/extruders/currentStatus", {
      signal,
    })
    .then((r) => r.data);
}

export function getBlendersCurrentStatus(signal?: AbortSignal) {
  return api
    .get<BlendersStatusResponse>("/blender/currentStatus", { signal })
    .then((r) => r.data);
}

export function getBlenderUtilization(signal?: AbortSignal) {
  return api
    .get<UtilizationResponse>("/blender/utilization", { signal })
    .then((r) => r.data);
}

/** ---- Grouped flow endpoint ----
 * Server expects query keys: start, end, group_by (hour|day|week|month), blender_id ("1"|"2"|"5")
 * Example row: { period: "2025-09-18T06:00:00Z", delta_value1_kg: 10, delta_value2_kg: 5, delta_value3_kg: 1 }
 */
export type BlenderFlowRow = {
  period: string | number | Date;
  delta_value1_kg?: number | null;
  delta_value2_kg?: number | null;
  delta_value3_kg?: number | null;
};

/** Frontend-friendly params (what many pages call) */
type FlowReqFrontend = {
  startISO: string; // ISO
  endISO: string; // ISO
  groupBy: "hour" | "day" | "week" | "month";
  blenderId: string; // e.g. "1" | "2" | "5"
  signal?: AbortSignal;
};

/** Backend-style params (legacy callers) */
type FlowReqBackend = {
  start: string; // ISO
  end: string; // ISO
  group_by: "hour" | "day" | "week" | "month";
  blender_id: "1" | "2" | "5";
  signal?: AbortSignal;
};

export type GetBlenderFlowParams = FlowReqFrontend | FlowReqBackend;

function isFrontendParams(p: GetBlenderFlowParams): p is FlowReqFrontend {
  return (p as FlowReqFrontend).startISO !== undefined;
}

/**
 * Accepts either frontend-style (startISO/endISO/groupBy/blenderId)
 * or backend-style (start/end/group_by/blender_id) and normalizes to the
 * server’s expected query keys.
 */
export function getBlenderFlow(params: GetBlenderFlowParams) {
  const signal = params.signal;

  const query = isFrontendParams(params)
    ? {
        start: params.startISO,
        end: params.endISO,
        group_by: params.groupBy,
        blender_id: params.blenderId,
      }
    : {
        start: params.start,
        end: params.end,
        group_by: params.group_by,
        blender_id: params.blender_id,
      };

  return api
    .get<BlenderFlowRow[]>("/blender/flow", { params: query, signal })
    .then((r) => r.data);
}
